import React, {forwardRef, useContext, useEffect, useImperativeHandle, useReducer, useRef, useState} from "react";
import {
    ArrowUpOutlined,
    CheckOutlined,
    CloseOutlined,
    DownloadOutlined,
    ExportOutlined,
    FormOutlined,
    MinusSquareOutlined,
    RedoOutlined,
    RightSquareOutlined,
    SaveOutlined,
} from '@ant-design/icons';
import {Affix, Button, Drawer, Dropdown, Menu,  notification, Space, Spin, Tooltip,} from "antd";
import ImagePlacer from "./ImagePlacer";
import {delay, omit, pick, capitalize, concat, isFunction, get, startCase, toLower} from "lodash";
import MediaSetter from "./MediaSetter";
import {SpecAdAction} from "../share/SpecAdAction";
import {findAdvertiserLogo} from "../aodApi/aodUtils";
import {objectReplaceReducer, shallowReducer} from "../lib/Utils";
import QuarkRender from "../services/quarkRender";
import {useDebouncedEffect} from "../uiUtils/hooks";
import hash from "object-hash";
import {ClientBridge} from "../index";
import {ContentEditor} from "./ContentEditor";
import {getDownloadMenu, getShareMenu, PublicImage} from "../uiUtils/Helpers";
import {AodIcon} from "../AodUi/AodIcon";
import {useMccSearch} from "../mccSearch/mccHooks";
import {AppController} from "../App";

const defaultOutput = {width: 0, height: 0, color: 'Color'};
const defaultAdInfo = {medium:'Print', standard:'', sizeName:'', columns:0};

function SpecAdRender(
    {
        template,  setTemplate,
        advertiser, advSsResults,
        lastRenderInfo= {}, setLastRenderInfo,
        logo= {}, setLogo,
        salesSheet, setSalesSheet,
        specAd, setSpecAd,
        renderHash, setRenderHash,
        doOrSave,
        visible,
    }, ref)
{
    const {mcc, aodService, wizard, mccNav} = useContext(ClientBridge);
    const controller = useContext(AppController);
    const [assetAuth,  setAssetAuth] = useState(true);

    const [sizePos,setSizePos] = useReducer(shallowReducer,{});
    // Modes
    // position - actively placing logo
    // render - server is re-rendering - show stupid dust OR just overlay logo and add a spinner
    // ready - render complete
    const [mode,setMode] = useState('ready');
    const [customize, setCustomize] = useState(false);
    const [action, setAction] = useState('');
    const doneRef = useRef();  // todo testing...

    //*********

    // renderBag
    const [adContent, setAdContent] = useState({company:{}});  // i.e. advertiser company
    const [output, setOutput] = useReducer(objectReplaceReducer, defaultOutput);
    const [socialMedia, setSocialMedia] = useState(true);
    const [adInfo, setAdInfo] = useReducer(objectReplaceReducer, defaultAdInfo );
    const [overlay, setOverlay] = useState({file: null});

    const [renderStatus, setRenderStatus] = useState('ready'); ///actively rendering
    const [renderTickler, setRenderTickler] = useState(0);
    const pdfUrl = useRef();
    const loadedSpecAd = useRef(false);
    const [keepAspectRatio, setKeepAspectRatio] = useState(false);

    const [campaignQuery, setCampaignQuery] = useState();
    const {data: result, isPending:loading, setData} = useMccSearch(campaignQuery);

    // cheap singleton
    const [quarkRender] = useState(() => new QuarkRender({
        baseURL: mcc.quarkRenderServer
    }));

    const canSendToAod= renderHash && advertiser;
    const canSendToAdSeller = !!get(wizard,  'adSeller.presentation.id') ;
    const adState = specAd
        ? ( specAd.renderInfo.renderHash===renderHash
            ? 'saved'
            : 'changed' )
        : 'new';

    useImperativeHandle(ref, () => ({
        resetRender,
        save: (onDone) => {
            doneRef.current = onDone;
            setAction(getSaveAction());
        },
        loadTemplate
    }));

    function onActionDone(){
        setAction('');
        if (isFunction(doneRef.current)){
            doneRef.current();
        }
        doneRef.current=undefined;
    }

    function getSaveAction(){
        return specAd && !specAd.locked ? 'Save' : 'Save As'; // assumes changes if specAd exists
    }

    //todo swicth ALL inch to widthInches
    useEffect(()=> {
        let query;
        if (template) {

            if (template.hasWebAd() || template.isWebAd()){

                // todo query reducer (don't re-search same campaign)
                const file_name = concat(template.getPrintNames(), template.getWebNames());
                query = {
                    filters:{
                        file_name,
                        has_miad: ['has miad'],
                        color: ['color'],
                        metro_type: ['spec ad'],
                    },
                    pager: {order: 'latest'}
                };
            }

            setAssetAuth(wizard.assetAuth(template));
        }
        if (!query) setData({records:[] , total:0});
        setCampaignQuery(query);
    }, [template]);

    /*
    // don't match ads now we have save & Save As - different paradigm
    useEffect(() => {
        if (renderHash && salesSheet){
            //Note:  it is possible to save a duplicate ad as the SS to saved is chosen until after the ad is rendered
            // So grab the most recent entry
            const matchedSpecAd = findLast(salesSheet.specAds, sa=>renderHash===sa.renderInfo.renderHash)

            if (matchedSpecAd){
                console.log('RenderHash: '+renderHash);
                setSpecAd(matchedSpecAd);
            }
        }
    }, [renderHash, salesSheet]);
     */


    useEffect(()=>{

        async function addLogo(){
            const logoMf = await findAdvertiserLogo(advertiser, aodService);
            setLogo({...logoMf, name:advertiser.name});
        }

        if (advertiser && advertiser.id) {
            addLogo();
            setAdContent({company:advertiser.company});
            //console.log('adcontent from advertiser');
        }
        setOverlay({});

    }, [advertiser]);

    useEffect(()=>{
        if (logo){
            setOverlay(overlay => overlay.aodAsset
            ? {
                aodAsset: pick(logo, 'id', 'fileName', 'fileClass', 'fileType', 'fileAspect', 'filePath',
                    'ownerType', 'ownerId'),
                position: overlay.position
            }
            : overlay
            );
        }
    },[logo]);

/*
    // Initiate render
    // debounce as changing some of the dependencies effect others
    // e.g. template resets output dimensions
    // also copes with several rapid changes of ad size etc
    useDebouncedEffect(() => {

        if (!template || !advertiser || !output.width) {
            return;
        }

        if (loadedSpecAd.current) {
            // short circuit render if we just restored an ad
            loadedSpecAd.current = false;
            return ;
        }

        //const renderBag = {template, overlay, socialMedia, output, adContent};
        // Omit overlay - we'll handle those in HTML until saving
        const renderBag = {template, socialMedia, output, adContent};
        const newHash = hash(getRenderRequest(renderBag));
        pdfUrl.current = null;
        const request = getRenderRequest(
            renderBag,
            {scale: "200", format: "jpg"}
        );
        setRenderStatus('rendering');

        quarkRender.render(request)
            .then((res) => {
                // Todo - seperate thumbnail URL
                setLastRenderInfo({
                    count: lastRenderInfo.count + 1,
                    //name: template.file_base,
                    thumbUrl: getPreviewUrl(res, lastRenderInfo.count + 1),
                    previewUrl: getPreviewUrl(res, lastRenderInfo.count + 1),
                });
                delay(() => setRenderStatus('done'), 500);
            })
            .catch(err => {
                setRenderStatus('failed');
                notification.error({
                    message: 'Generate Spec Ad failed',
                    description: JSON.stringify(err)
                });
            })
            .finally(() => {
                setRenderHash( newHash);
                //console.dir(getRenderRequest(renderBag));
            });
    }, 1000, [adContent, output, template, socialMedia, advertiser, renderTickler]);
 */

    // Initiate render
    // debounce as changing some of the dependencies effect others
    // e.g. template resets output dimensions
    // also copes with several rapid changes of ad size etc
    useDebouncedEffect(() => {
         if (!template || !advertiser || !output.width) {
            return;
        }

        console.log('Render: ',output);
        //const renderBag = {template, overlay, socialMedia, output, adContent};
        // Omit overlay - we'll handle those in HTML until saving
        const renderBag = {template, socialMedia, output, adContent};
        pdfUrl.current = null;
        const request = getRenderRequest(
            renderBag,
            {scale: "200", format: "jpg"}
        );

        if (loadedSpecAd.current) {
            // quietly recreate "background"
            loadedSpecAd.current = false;
        } else {
            setRenderStatus('rendering');
        }

        quarkRender.render(request)
            .then((res) => {
                // Todo - seperate thumbnail URL
                setLastRenderInfo({
                    count: lastRenderInfo.count + 1,
                    //name: template.file_base,
                    thumbUrl: getPreviewUrl(res, lastRenderInfo.count + 1),
                    previewUrl: getPreviewUrl(res, lastRenderInfo.count + 1),
                });
                delay(() => setRenderStatus('done'), 500);
            })
            .catch(err => {
                setRenderStatus('failed');
                notification.error({
                    message: 'Generate Spec Ad failed',
                    description: JSON.stringify(err)
                })

                // todo reset renderHash?
            });
    }, 1000, [adContent, output, template, socialMedia, advertiser, renderTickler]);

    useEffect(() => {
        if (!template || !advertiser || !output.width) {
            return;
        }

        const renderBag = {template, overlay, socialMedia, output, adContent};
        const newHash = hash(getRenderRequest(renderBag));
        setRenderHash( newHash);
        pdfUrl.current = null;
    }, [adContent, output, template, socialMedia, advertiser, overlay]);

    function resetRender({specAd, template}){

        //note template (& advertiser managed above)
        if (specAd){
            const {overlay, adInfo, socialMedia, adContent, output, template, renderHash} = specAd.renderInfo; // note local values

            setSocialMedia(socialMedia);
            setAdContent(adContent);  // Note this works as reset call above is delayed (useEffect advertiser)
            setOverlay(overlay);
            setOutput(output);
            setAdInfo(adInfo);  // medium etc todo - rename

            // todo - Fudge to deal with pre-release ads lacking data
            if (! ('metro_type' in template)) template.metro_type='Spec Ad';
            setTemplate( new mcc.Asset(template));  //todo  ?does stored template have enough info

            setRenderHash(renderHash);
            setSpecAd(specAd);

            setLastRenderInfo({
                count: lastRenderInfo.count + 1,
                //name: template.file_base,
                thumbUrl: specAd.thumbUrl,
                previewUrl: specAd.previewUrl,
            });
            loadedSpecAd.current=true;

        } else {

            if (template) {
                loadTemplate(template);
            } else {
                setOutput(defaultOutput);  // probably don't need this
                setAdInfo(defaultAdInfo);
                setTemplate();
            }
            //setAdContent(oldAdv ?oldAdv.company :{});  // useEffect [advertiser] fills/blanks this
            setSocialMedia(true);
            setOverlay({file: null});

            setRenderHash();
            setSpecAd();
            setLastRenderInfo({count: lastRenderInfo.count});
        }

        pdfUrl.current = null;
        setKeepAspectRatio(false);
        setRenderStatus('ready');
    }


    function loadTemplate(template) {
        const {widthInches: width, heightInches: height} = template;
        setOutput({width, height});  //, color: 'Color'}); //
        setAdInfo({medium: capitalize(template.medium)}); // MMC uses lowercase..
        setTemplate(template);
    }


    function getPreviewUrl(res, renderCount) {
        // return '/' +res.localUrl+'?count='+count ; // ?wouldn't this be sufficient
        // return quarkRender.config.baseURL+res.localUrl+'?count='+count ;
        return res.remoteUrl + '?count=' + renderCount;
    }

    async function getPdfUrl() {

        if (pdfUrl.current) return pdfUrl.current;

        setRenderStatus('rendering');
        const renderBag = {template, overlay, socialMedia, output, adContent};
        const request = getRenderRequest(renderBag);

        try {
            const res = await quarkRender.render(request);
            pdfUrl.current = res.localUrl;
            return pdfUrl.current;
        } catch(err) {
            setRenderStatus('failed');
            throw err;
        } finally {
            delay(() => setRenderStatus('done'), 500);
        }
    }

    async function getNewSpecAd() {
        const renderBag = {template, overlay, socialMedia, output, adContent};
        const buildExtras = {adInfo, renderHash};

        return buildSpecAd({...renderBag, ...buildExtras});
    }

    function forceRender() {
        setRenderTickler(renderTickler + 1);
    }

    const sendToAod = ()=> {
        notification.open({
            message: 'Something Happened',
            description: JSON.stringify(sizePos)
        });
        setMode('ready');
    };

    const placeLogo = ()=> {

        const bottom = Math.min(sizePos.y + sizePos.height, 1);
        const right = Math.min(sizePos.x + sizePos.width, 1);

        setOverlay({
            //"file": "expansion:docpool:live:N1908A14006C:N1801P00901C.eps",
            aodAsset: pick(logo,'id','fileName','fileClass','fileType','fileAspect', 'filePath',
                'ownerType', 'ownerId'),
            "position": {
                "top": sizePos.y,
                "left": sizePos.x,
                bottom,
                right
            }
        });
        setMode('ready');
    };
    const removeLogo = ()=> {
        setOverlay({});
        setMode('ready');
    };

    const onSave = ({salesSheet, specAd: newSpecAd})=>{
        if (salesSheet) {
            setSalesSheet(salesSheet);
        }
        if ( specAd) {
            console.log('Spec Ad old: ', specAd.id, ' ', specAd.recordVersion);
        }
        if ( newSpecAd) {
            console.log('Spec Ad new: ', newSpecAd.id, ' ', newSpecAd.recordVersion);
            setSpecAd(newSpecAd);
        }
    };

    if (!visible && mode==='position'){
        setMode('ready');
    }

    async function adSellerSelect(){
        const pdfUrl = await getPdfUrl();
        const newSpecAd = await getNewSpecAd();
        newSpecAd.notes = 'saved to Pulse on ' + Date().toString();
        newSpecAd.locked = false;  // todo ? set in AdSeller isn't that lock worthy
        await controller.createSpecAd(newSpecAd,salesSheet,pdfUrl);
        // don't need to set specAd / OnSave etc - we're leaving...
        wizard.adSellerSelect(template);
    }

    function onDownloadAction(specAd,action){
        if (action==='Info View'){
            mccNav.infoView(template);
        }else {
            setAction(action);
        }
    }

    // todo use overlay to maintain "similar" position in new template / changes dimensions
    const disableAll = !(lastRenderInfo && lastRenderInfo.previewUrl);

    const shareMenu=getShareMenu({item:specAd, onAction:(ad,action)=>setAction(action)});
    const downloadMenu=getDownloadMenu({item:specAd, onAction:onDownloadAction});

    const campaignMenu = template && result && (result.total > 1) && (
        <Menu>
            {result.records.map( t =>
                <Menu.Item
                    key={t.file_base}
                    onClick={()=>doOrSave(()=>loadTemplate(t))}
                    disabled={t.file_base===template.file_base}
                >{startCase(toLower(t.ad_size))} {t.dimensions}</Menu.Item>
            )}
        </Menu>
    );

    return (
        <div>
            <Spin spinning={renderStatus === 'rendering' && !disableAll}
                  indicator={<PublicImage path={'wizard-wand-dust.gif'} alt={'fwizard'}
                    style={{
                        width: 'unset',
                        height: 'unset',
                        position: 'unset',
                        top: 'unset',
                        left: 'unset',
                    }}/>}
            >
                {disableAll
                    ? <div style={{minHeight:400}}>
                        { renderStatus === 'rendering' &&
                            <PublicImage path={'wizard-wand-dust.gif'} alt={'fwizard'} />}
                        </div>
                    : <Tooltip title={(logo && logo.thumbUrl)?'Click on this preview to place logo':''}>
                        <ImagePlacer
                            onChange={setSizePos}
                            percentCoordinates={overlay && overlay.position }
                            backgroundUrl={lastRenderInfo.previewUrl}
                            isGrayscale={output.color==='Black & White'}
                            overlayUrl={logo.thumbUrl}
                            mode={mode} setMode={setMode}
                    /></Tooltip>
                }
                <Affix>{mode === 'position'
                    ?
                    <Space>
                        <span>Position Logo: </span>
                        <Button icon={<CloseOutlined/>} onClick={()=>setMode('ready')}>Cancel</Button>
                        <Button icon={<MinusSquareOutlined/>} onClick={removeLogo}>Remove</Button>
                        <Button type='primary' icon={<CheckOutlined/>} onClick={placeLogo}>Done</Button>
                    </Space>
                    :
                    <Space>
                        <Tooltip title={'refresh spec ad'}>
                            <Button size="small" icon={<RedoOutlined/>} style={{marginRight: 10}} shape="circle"
                                    onClick={forceRender}
                                    type={renderStatus === 'failed' ? 'primary' : 'dashed'}
                                    disabled={disableAll}
                            />
                        </Tooltip>
                        <Dropdown overlay={shareMenu}
                                  disabled={disableAll || renderStatus === 'failed'}>
                            <Button icon={<ExportOutlined/>} type={'primary'}>Share</Button>
                        </Dropdown>
                        <Dropdown overlay={downloadMenu}
                                  disabled={disableAll || renderStatus === 'failed'}>
                            <Button icon={<DownloadOutlined/>} type={'primary'}>Download</Button>
                        </Dropdown>
                        {false && <Button icon={<RightSquareOutlined/>} type={'primary'} onClick={sendToAod}
                                          disabled={canSendToAod || disableAll}>Send to AOD</Button>}

                        {campaignMenu &&
                            <Dropdown overlay={campaignMenu}
                                      disabled={disableAll || renderStatus === 'failed'}>
                                <Button icon={<AodIcon type={'template'}/>} type={'primary'}>
                                    Ad Options</Button>
                            </Dropdown>
                        }

                        <Tooltip title={'Change advertiser text, place a logo, resize & more'}>
                            <Button icon={<FormOutlined />} type={'primary'} disabled={disableAll}
                                onClick={() => setCustomize(true)}
                            >Customize</Button>
                        </Tooltip>

                        <Button icon={<SaveOutlined/>} type={'primary'}
                                onClick={()=>setAction(adState==='new' ?'Save As':'Save')}
                                disabled={adState==='saved' || disableAll}
                        >Save</Button>
                        <Tooltip title={"Save as a new ad in presentation"}>
                            <Button icon={<SaveOutlined/>} type={'primary'}
                                    onClick={()=>setAction('Save As')}
                                    disabled={adState==='new' || disableAll}
                            >Save As</Button>
                        </Tooltip>

                        {canSendToAdSeller && <Tooltip title={'Return to Pulse website and store this ad for the presentation'}>
                            <Button icon={<ArrowUpOutlined rotate={45} />} type={'primary'}
                                    onClick={adSellerSelect}
                                    disabled={disableAll}> Save Ad in Pulse</Button>
                        </Tooltip>}
                    </Space>}
                </Affix>
                <div style={{margin:10, fontSize:"smaller", color:'gray'}}
                    >Developed by Metro Creative Graphics, Inc.</div>
            </Spin>

            <Drawer placement={'right'} width={400} bodyStyle={{backgroundColor:'whitesmoke'}}
                    visible={customize} onClose={() => setCustomize(false)}
                    forceRender={true}
            >
                <ContentEditor
                    company={adContent.company} onChangeCompany={company => setAdContent({company})}
                    logo={logo} setLogo={setLogo}
                    advertiser={advertiser}
                    onClickPositionLogo={() => {
                        setMode('position');
                        setCustomize(false)
                    }}
                    disabled={disableAll} aodAsset={overlay.aodAsset}
                    onClickRemoveLogo={removeLogo}
                    socialMedia={socialMedia} setSocialMedia={setSocialMedia}
                />
                <div style={{height: 25}}/>
                <MediaSetter template={template}
                             output={output} onOutputChange={setOutput}
                             adInfo={adInfo} onMediumChange={setAdInfo}
                             keepAspectRatio={keepAspectRatio} setKeepAspectRatio={setKeepAspectRatio}
                />
            </Drawer>

            <SpecAdAction advertiser={advertiser} advSsResults={advSsResults}
                          salesSheet={salesSheet} specAd={specAd}
                          getPdfUrl={getPdfUrl} getNewSpecAd={getNewSpecAd}
                          changes={adState==='changed'}
                          onSave={onSave}
                          action={action} onActionDone={onActionDone}
                          assetAuth={assetAuth}
            />
        </div>
    );
}
export default SpecAdRender = forwardRef(SpecAdRender);

/*
 <Button icon={<SaveOutlined/>} type={'primary'} onClick={()=>setAction('Save')}
                                disabled={!specAd || specAd.renderInfo.renderHash===renderHash || disableAll}>Save</Button>
                        <Button icon={<SaveOutlined/>} type={'primary'} onClick={()=>setAction('Save As')}
                                disabled={disableAll}>Save As</Button>

<span>{adState} {renderHash} {get(specAd, "renderInfo.renderHash")}</span>
 */



// <ShareDialogs {...{shareAction,setShareAction, advertiser}} {...specAdInfo} />

export function getRenderRequest(
    {template, overlay={}, socialMedia, output, adContent},
    {format='pdf', scale="100"}={}
    )
{  //todo color?
    return {
        template: template.file_base,
        overlays: (overlay.file || overlay.aodAsset) ? [overlay] : [],
        options: {
            omitSocialMedia: !socialMedia
        },
        output: {
            height: output.height,
            width: output.width,
            scale,
            format, //"jpg"  //todo read output
            color: output.color === 'Black & White' ? 'grayscale' : ''
        },
        content: adContent
    };
}

function buildSpecAd(
    {
        template,
        overlay,
        socialMedia,
        output,
        adContent,
        adInfo,
        renderHash,
        //shareInfo={}
    })
{
    const templateProps = [
        'file_base', "file_ext", "file_name",'name',
        'width','height', 'widthInches', 'heightInches',
        'medium',
        "metro_type", "web_ad_size", "ad_size",
        "group", "service", "related", 'extra',
        "service"
    ];

    const renderData = JSON.stringify({
        template: pick(template,templateProps),
        overlay,
        socialMedia,
        output,
        adContent,
        adInfo,
        renderHash
    }, null, 2);

    // todo should redenderData include info stored directly on Ad?
    return {
        metroSpecAd: template.file_base,
        //...pick(shareInfo,'jobNumber', 'notes'),
        renderData,
        ...pick(adInfo, 'medium', 'columns', 'standard'),
        ...pick(output, 'width', 'height'),
        blackAndWhite: output.color === 'Black & White',
        company: omit(adContent.company, 'id', 'recordVersion')
    };
}